import React from "react";
import {
  ParamNatureEnum,
  SppMacroGroupEnum,
  TargetSectionEnum,
} from "../../../classes/enums/SppEnum";
import WidgetBox from "../../../components/widgetBox/WidgetBox";
import CustomSppParameter from "./CustomSpp";
import SimpleSpp from "./SimpleSpp";
import SppParameter from "./Spp";
import "./SppWidget.css";

const categoriesGap = [
  { id: TargetSectionEnum.VALUES, label: "Values" },
  { id: TargetSectionEnum.SCORES, label: "Scores" },
];

const GapCategoryTabs = ({ activeCategory, onCategoryChange, disabled }) => {
  return (
    <div className="gap-category-tabs">
      <div className="gap-category-tab-wrapper">
        {categoriesGap.map(category => (
          <button
            key={category.id}
            className={`gap-category-button ${
              activeCategory === category.id ? "active" : ""
            }`}
            onClick={() => onCategoryChange(category.id)}
            type="button"
            disabled={disabled}
          >
            {category.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const categoriesSaas = [
  { id: TargetSectionEnum.POINTS, label: "Points" },
  { id: TargetSectionEnum.DEVIATION, label: "Deviation" },
];

const SaasCategoryTabs = ({ activeCategory, onCategoryChange, disabled }) => {
  return (
    <div className="gap-category-tabs">
      <div className="gap-category-tab-wrapper">
        {categoriesSaas.map(category => (
          <button
            key={category.id}
            className={`gap-category-button ${
              activeCategory === category.id ? "active" : ""
            }`}
            onClick={() => onCategoryChange(category.id)}
            type="button"
            disabled={disabled}
          >
            {category.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const SppParameterGroup = ({
  title,
  category,
  parameters,
  onSppSelected,
  onSppSelectedCustom,
  activeParameters,
  isCustom = false,
  isSimple = false,
  isOctView = false,
  hideHeader = false,
  removeCustomParameter,
  onToggleFavorite,
  favoriteParams,
  selectedTargetType,
  allowFavorites = false,
  isSettingsMode = false,
  compareMode = false,
  currentPlanLabel = "Plan",
  comparedPlanLabel = "Plan",
  disabled = false,
}) => {
  const [gapCategory, setGapCategory] = React.useState(
    TargetSectionEnum.VALUES
  );

  const [saasCategory, setSaasCategory] = React.useState(
    TargetSectionEnum.POINTS
  );

  if (isSimple && !isSettingsMode) {
    return (
      <div className="simple-spp-group">
        {parameters.map(param => (
          <SimpleSpp key={param.key} param={param} />
        ))}
      </div>
    );
  }

  const renderStandardGroup = () => {
    return (
      <div className="spp-parameter-group">
        {parameters.map(param => (
          <SppParameter
            key={param.key}
            param={param}
            onSppSelected={onSppSelected}
            isVisible={activeParameters[param.key]}
            isOctView={isOctView}
            onToggleFavorite={onToggleFavorite}
            favoriteParams={favoriteParams}
            selectedTargetType={selectedTargetType}
            allowFavorites={allowFavorites}
            isSettingsMode={isSettingsMode}
            compareMode={compareMode}
            currentPlanLabel={currentPlanLabel}
            comparedPlanLabel={comparedPlanLabel}
            disabled={disabled}
          />
        ))}
      </div>
    );
  };

  const renderCustomGroup = () => {
    return (
      <div className="spp-parameter-group">
        {parameters.map(param => (
          <CustomSppParameter
            key={param.key || param.unique_identifier}
            param={param}
            onSppSelected={onSppSelectedCustom}
            isVisible={activeParameters[param.key]}
            onRemove={() => removeCustomParameter(param, category)}
            disabled={disabled}
          />
        ))}
      </div>
    );
  };

  const renderGapGroup = () => {
    const gapPrameter = parameters.find(
      param => param.shortName === "GAP_score"
    );

    const otherParameters = parameters.filter(
      param => param.shortName !== "GAP_score"
    );

    const scores = otherParameters.filter(
      param =>
        param.nature === ParamNatureEnum.SCORE ||
        param.nature === ParamNatureEnum.CLASSIFICATION
    );

    const values = otherParameters.filter(
      param =>
        param.nature === ParamNatureEnum.ANGLE ||
        param.nature === ParamNatureEnum.DISTANCE ||
        param.nature === ParamNatureEnum.INDEX
    );

    return (
      <div className="spp-parameter-group">
        <SppParameter
          param={gapPrameter}
          onSppSelected={onSppSelected}
          isVisible
          isOctView={isOctView}
          onToggleFavorite={onToggleFavorite}
          favoriteParams={favoriteParams}
          selectedTargetType={selectedTargetType}
          allowFavorites={allowFavorites}
          isSettingsMode={isSettingsMode}
          compareMode={compareMode}
          currentPlanLabel={currentPlanLabel}
          comparedPlanLabel={comparedPlanLabel}
          disabled={disabled}
        />

        <GapCategoryTabs
          activeCategory={gapCategory}
          onCategoryChange={setGapCategory}
          disabled={disabled}
        />

        {gapCategory === TargetSectionEnum.VALUES ? (
          <div className="gap-values">
            {values.map(param => (
              <SppParameter
                key={param.key}
                param={param}
                onSppSelected={onSppSelected}
                isVisible={activeParameters[param.key]}
                isOctView={isOctView}
                onToggleFavorite={onToggleFavorite}
                favoriteParams={favoriteParams}
                selectedTargetType={selectedTargetType}
                allowFavorites={allowFavorites}
                isSettingsMode={isSettingsMode}
                compareMode={compareMode}
                currentPlanLabel={currentPlanLabel}
                comparedPlanLabel={comparedPlanLabel}
                disabled={disabled}
              />
            ))}
          </div>
        ) : (
          <div className="gap-scores">
            {scores.map(param => (
              <SppParameter
                key={param.key}
                param={param}
                onSppSelected={onSppSelected}
                isVisible={activeParameters[param.key]}
                isOctView={isOctView}
                onToggleFavorite={onToggleFavorite}
                favoriteParams={favoriteParams}
                selectedTargetType={selectedTargetType}
                allowFavorites={allowFavorites}
                isSettingsMode={isSettingsMode}
                compareMode={compareMode}
                currentPlanLabel={currentPlanLabel}
                comparedPlanLabel={comparedPlanLabel}
                disabled={disabled}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSaasGroup = () => {
    const saasParameter = parameters.find(
      param => param.shortName === "SAAS_score"
    );
    const otherParameters = parameters.filter(
      param => param.shortName !== "SAAS_score"
    );

    const points = otherParameters.filter(
      param =>
        param.nature === ParamNatureEnum.SCORE ||
        param.nature === ParamNatureEnum.CLASSIFICATION
    );

    const deviation = otherParameters.filter(
      param =>
        param.nature === ParamNatureEnum.ANGLE ||
        param.nature === ParamNatureEnum.DISTANCE
    );

    return (
      <div className="spp-parameter-group">
        <SppParameter
          param={saasParameter}
          onSppSelected={onSppSelected}
          isVisible
          isOctView={isOctView}
          onToggleFavorite={onToggleFavorite}
          favoriteParams={favoriteParams}
          selectedTargetType={selectedTargetType}
          allowFavorites={allowFavorites}
          isSettingsMode={isSettingsMode}
          compareMode={compareMode}
          currentPlanLabel={currentPlanLabel}
          comparedPlanLabel={comparedPlanLabel}
        />
        <SaasCategoryTabs
          activeCategory={saasCategory}
          onCategoryChange={setSaasCategory}
          disabled={disabled}
        />

        {saasCategory === TargetSectionEnum.POINTS ? (
          <div className="saas-points">
            {points.map(param => (
              <SppParameter
                key={param.key}
                param={param}
                onSppSelected={onSppSelected}
                isVisible={activeParameters[param.key]}
                isOctView={isOctView}
                onToggleFavorite={onToggleFavorite}
                favoriteParams={favoriteParams}
                selectedTargetType={selectedTargetType}
                allowFavorites={allowFavorites}
                isSettingsMode={isSettingsMode}
                compareMode={compareMode}
                currentPlanLabel={currentPlanLabel}
                comparedPlanLabel={comparedPlanLabel}
                disabled={disabled}
              />
            ))}
          </div>
        ) : (
          <div className="saas-deviation">
            {deviation.map(param => (
              <SppParameter
                key={param.key}
                param={param}
                onSppSelected={onSppSelected}
                isVisible={activeParameters[param.key]}
                isOctView={isOctView}
                onToggleFavorite={onToggleFavorite}
                favoriteParams={favoriteParams}
                selectedTargetType={selectedTargetType}
                allowFavorites={allowFavorites}
                isSettingsMode={isSettingsMode}
                compareMode={compareMode}
                currentPlanLabel={currentPlanLabel}
                comparedPlanLabel={comparedPlanLabel}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = category => {
    const getContentByCategory = () => {
      switch (category) {
        case SppMacroGroupEnum.GLOBAL:
          return renderGapGroup();
        case SppMacroGroupEnum.SAAS:
          return renderSaasGroup();
        default:
          return renderStandardGroup();
      }
    };

    return (
      <div className="spp-parameter-group-container">
        <WidgetBox
          title={title}
          isExpandable={true}
          isSmall={true}
          noBgColor={true}
          noHorizontalPadding={true}
          toggleTitleColor={true}
          noHeader={hideHeader}
          name={`measurements-${category}`}
        >
          {isCustom ? renderCustomGroup() : getContentByCategory()}
        </WidgetBox>
      </div>
    );
  };

  return (
    <div className="spp-parameter-group-wrapper">{renderGroup(category)}</div>
  );
};

export default SppParameterGroup;
